import React, { useEffect, useState } from "react";
import { api, apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmpAvatar } from "@/components/EmpAvatar";
import { UploadButton } from "@/components/Upload";
import { PageTitle, SectionCard } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, Wallet } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [contact, setContact] = useState("");
  const [address, setAddress] = useState("");
  const [advances, setAdvances] = useState([]);

  const load = () => api.get("/employees/me").then((r) => {
    setEmp(r.data); setContact(r.data.contact_number || ""); setAddress(r.data.address || "");
    api.get(`/employees/${r.data.id}/advances`).then((a) => setAdvances(a.data)).catch(() => {});
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    try {
      await api.put(`/employees/${emp.id}`, { contact_number: contact, address });
      toast.success("Profile updated");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const changePw = async () => {
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const uploadPhoto = async (f) => {
    await api.put(`/employees/${emp.id}`, { photo_file_id: f.id });
    toast.success("Photo updated");
    load();
  };

  if (!emp) return null;

  return (
    <div className="animate-fade-up max-w-3xl">
      <PageTitle overline="Account" title="My Profile" />
      <div className="space-y-6">
        <SectionCard title="Profile Photo & Details">
          <div className="flex items-center gap-4 mb-5">
            <EmpAvatar emp={emp} size="h-16 w-16" />
            <div>
              <p className="font-heading font-bold text-lg">{emp.full_name}</p>
              <p className="text-sm text-muted-foreground">{emp.role} · {emp.department}</p>
            </div>
            <div className="ml-auto"><UploadButton label="Change Photo" accept="image/*" onUploaded={uploadPhoto} /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Contact Number</Label><Input value={contact} onChange={(e) => setContact(e.target.value)} className="mt-1.5" data-testid="profile-contact" /></div>
            <div><Label>Address</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1.5" data-testid="profile-address" /></div>
          </div>
          <Button onClick={saveProfile} className="mt-4" data-testid="save-profile"><Save className="h-4 w-4 mr-1.5" /> Save</Button>
        </SectionCard>

        {advances.length > 0 && (
          <SectionCard title="My Salary Advances">
            <div className="space-y-3">
              {advances.map((adv) => {
                const pct = adv.amount ? Math.min(100, Math.round((adv.recovered_to_date / adv.amount) * 100)) : 0;
                return (
                  <div key={adv.id} className="rounded-md border border-border p-3" data-testid={`my-advance-${adv.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Wallet className="h-4 w-4 text-primary" />
                      <p className="font-heading font-bold text-sm">{fmtMoney(adv.amount)}</p>
                      <span className="text-xs text-muted-foreground">· paid out {adv.disbursement_period} · over {adv.months} month(s)</span>
                      <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded ${adv.remaining <= 0 ? "bg-success/10 text-success" : "bg-warning/10 text-warning"}`} data-testid={`my-advance-remaining-${adv.id}`}>
                        {adv.remaining <= 0 ? "Fully recovered" : `${fmtMoney(adv.remaining)} remaining`}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">Recovered {fmtMoney(adv.recovered_to_date)} of {fmtMoney(adv.amount)} · {fmtMoney(adv.monthly_amount)}/mo · ends {adv.last_recovery_period}{adv.reason ? ` · ${adv.reason}` : ""}</p>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}

        <SectionCard title="Change Password">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Current Password</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-1.5" data-testid="current-pw" /></div>
            <div><Label>New Password</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1.5" data-testid="new-pw" /></div>
          </div>
          <Button onClick={changePw} disabled={!pw.current_password || !pw.new_password} className="mt-4" data-testid="change-pw-btn">Update Password</Button>
        </SectionCard>
      </div>
    </div>
  );
}
